/**
 * SSO Provider Setup Wizard
 *
 * Multi-step wizard for configuring Microsoft Entra ID or Google SSO providers.
 * Steps: Instructions -> Configuration -> Test Connection -> Activate
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { Spinner } from '../../../components/ui/Spinner';
import { Checkbox } from '../../../components/ui/Checkbox';
import { SSOProviderType } from '../../../types/sso.types';
import type { SSOProviderCreateDTO, SSOTestConnectionResponse } from '../../../types/sso.types';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '../../../contexts/AuthContext';
import { useRoles } from '../../roles/hooks/useRoles';

// ============================================================================
// TYPES
// ============================================================================

interface SSOProviderSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  providerType: typeof SSOProviderType.MICROSOFT_ENTRA | typeof SSOProviderType.GOOGLE;
  onSubmit: (data: SSOProviderCreateDTO) => Promise<void>;
  onTestConnection: (providerId: string) => Promise<SSOTestConnectionResponse>;
  isSubmitting?: boolean;
}

interface MicrosoftFormValues {
  displayName: string;
  clientId: string;
  clientSecret: string;
  entraDirectoryId: string;
  allowedDomains: string;
  enforceSSO: boolean;
  autoProvisionUsers: boolean;
  defaultRoles: string[];
}

interface GoogleFormValues {
  displayName: string;
  clientId: string;
  clientSecret: string;
  hostedDomain: string;
  allowedDomains: string;
  enforceSSO: boolean;
  autoProvisionUsers: boolean;
  defaultRoles: string[];
}

// ============================================================================
// STEP INDICATOR
// ============================================================================

const StepIndicator: React.FC<{ currentStep: number; totalSteps: number }> = ({
  currentStep,
  totalSteps,
}) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className="flex items-center">
          <div
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
              i + 1 === currentStep
                ? 'bg-primary-600 text-white'
                : i + 1 < currentStep
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-500'
            )}
          >
            {i + 1 < currentStep ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              i + 1
            )}
          </div>
          {i < totalSteps - 1 && (
            <div
              className={clsx(
                'w-12 h-0.5 mx-1',
                i + 1 < currentStep ? 'bg-green-500' : 'bg-gray-200'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MICROSOFT ENTRA ID INSTRUCTIONS
// ============================================================================

const MicrosoftInstructions: React.FC = () => (
  <div className="space-y-4">
    <h3 className="text-base font-semibold text-gray-900">
      Register an Application in Azure Portal
    </h3>
    <ol className="list-decimal list-inside space-y-3 text-sm text-gray-600">
      <li>
        Go to{' '}
        <a
          href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Azure Portal - App Registrations
        </a>
      </li>
      <li>Click <strong>"New registration"</strong></li>
      <li>
        Enter a name (e.g., "Ketrics SSO") and select <strong>"Accounts in this organizational directory only"</strong>
      </li>
      <li>
        Set the <strong>Redirect URI</strong> to:{' '}
        <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">
          {window.location.origin}/auth/sso/callback
        </code>
      </li>
      <li>Click <strong>"Register"</strong></li>
      <li>
        Copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong> from the Overview page
      </li>
      <li>
        Go to <strong>"Certificates & secrets"</strong> and create a new client secret. Copy the secret value immediately.
      </li>
      <li>
        Go to <strong>"API permissions"</strong> and ensure <strong>Microsoft Graph - User.Read</strong> is added (it should be by default)
      </li>
    </ol>
    <Alert variant="info">
      Make sure to copy the client secret value right after creation. You will not be able to see it again.
    </Alert>
  </div>
);

// ============================================================================
// GOOGLE INSTRUCTIONS
// ============================================================================

const GoogleInstructions: React.FC = () => (
  <div className="space-y-4">
    <h3 className="text-base font-semibold text-gray-900">
      Create OAuth Credentials in Google Cloud Console
    </h3>
    <ol className="list-decimal list-inside space-y-3 text-sm text-gray-600">
      <li>
        Go to{' '}
        <a
          href="https://console.cloud.google.com/apis/credentials"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Google Cloud Console - Credentials
        </a>
      </li>
      <li>Select or create a project</li>
      <li>
        Click <strong>"Create Credentials"</strong> and select <strong>"OAuth client ID"</strong>
      </li>
      <li>
        If prompted, configure the <strong>OAuth consent screen</strong> first (select "Internal" for Google Workspace organizations)
      </li>
      <li>
        Select <strong>"Web application"</strong> as the application type
      </li>
      <li>
        Add the following <strong>Authorized redirect URI</strong>:{' '}
        <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">
          {window.location.origin}/auth/sso/callback
        </code>
      </li>
      <li>Click <strong>"Create"</strong></li>
      <li>
        Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> from the confirmation dialog
      </li>
    </ol>
    <Alert variant="info">
      For Google Workspace organizations, setting the "Hosted Domain" restricts sign-in to users from your domain only.
    </Alert>
  </div>
);

// ============================================================================
// WIZARD COMPONENT
// ============================================================================

export const SSOProviderSetupWizard: React.FC<SSOProviderSetupWizardProps> = ({
  isOpen,
  onClose,
  providerType,
  onSubmit,
  onTestConnection,
  isSubmitting = false,
}) => {
  const [step, setStep] = useState(1);
  const [testResult, setTestResult] = useState<SSOTestConnectionResponse | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [createdProviderId, setCreatedProviderId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string>('');

  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId ?? '';
  const isMicrosoft = providerType === SSOProviderType.MICROSOFT_ENTRA;
  const totalSteps = 4;

  const { data: roles } = useRoles(tenantId);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<MicrosoftFormValues | GoogleFormValues>({
    defaultValues: isMicrosoft
      ? { displayName: '', clientId: '', clientSecret: '', entraDirectoryId: '', allowedDomains: '', enforceSSO: false, autoProvisionUsers: false, defaultRoles: [] }
      : { displayName: '', clientId: '', clientSecret: '', hostedDomain: '', allowedDomains: '', enforceSSO: false, autoProvisionUsers: false, defaultRoles: [] },
  });

  const autoProvisionUsers = watch('autoProvisionUsers');

  const handleClose = () => {
    setStep(1);
    setTestResult(null);
    setCreatedProviderId(null);
    setSubmitError('');
    reset();
    onClose();
  };

  const handleNext = () => {
    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleConfigSubmit = async (data: MicrosoftFormValues | GoogleFormValues) => {
    setSubmitError('');

    const domains = data.allowedDomains
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean);

    const defaultRoles = data.autoProvisionUsers && data.defaultRoles?.length > 0
      ? data.defaultRoles
      : undefined;

    const createData: SSOProviderCreateDTO = {
      providerType,
      displayName: data.displayName || undefined,
      clientId: data.clientId,
      clientSecret: data.clientSecret,
      allowedDomains: domains,
      enforceSSO: data.enforceSSO,
      autoProvisionUsers: data.autoProvisionUsers,
      defaultRoles,
      ...(isMicrosoft
        ? { entraDirectoryId: (data as MicrosoftFormValues).entraDirectoryId }
        : { hostedDomain: (data as GoogleFormValues).hostedDomain }),
    };

    try {
      await onSubmit(createData);
      // The parent will create the provider and pass back the ID
      // For now, move to test step
      handleNext();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to save provider configuration.'
      );
    }
  };

  const handleTestConnection = async () => {
    if (!createdProviderId) {
      // If we don't have a provider ID yet, skip to activate
      handleNext();
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await onTestConnection(createdProviderId);
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed.',
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header
        title={`Set up ${isMicrosoft ? 'Microsoft Entra ID' : 'Google'} SSO`}
        subtitle={`Step ${step} of ${totalSteps}`}
      />
      <Modal.Body>
        <StepIndicator currentStep={step} totalSteps={totalSteps} />

        {/* Step 1: Instructions */}
        {step === 1 && (
          <div>
            {isMicrosoft ? <MicrosoftInstructions /> : <GoogleInstructions />}
          </div>
        )}

        {/* Step 2: Configuration Form */}
        {step === 2 && (
          <form
            id="sso-config-form"
            onSubmit={handleFormSubmit(handleConfigSubmit)}
            className="space-y-4"
          >
            {submitError && (
              <Alert variant="error" dismissible onDismiss={() => setSubmitError('')}>
                {submitError}
              </Alert>
            )}

            <FormField
              label="Display Name"
              error={(errors as Record<string, { message?: string }>).displayName?.message}
              helperText={`A label to identify this provider (e.g., "${isMicrosoft ? 'Microsoft - Acme Corp' : 'Google - Acme Corp'}"). Optional.`}
            >
              <Input
                {...register('displayName' as 'clientId')}
                type="text"
                placeholder={isMicrosoft ? 'Microsoft Entra ID' : 'Google'}
                error={(errors as Record<string, { message?: string }>).displayName?.message}
              />
            </FormField>

            <FormField
              label="Client ID"
              required
              error={errors.clientId?.message}
              helperText={isMicrosoft ? 'Application (client) ID from Azure Portal' : 'OAuth 2.0 Client ID from Google Cloud Console'}
            >
              <Input
                {...register('clientId', { required: 'Client ID is required' })}
                type="text"
                placeholder={isMicrosoft ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' : 'xxxxxxxxxxxx.apps.googleusercontent.com'}
                error={errors.clientId?.message}
              />
            </FormField>

            <FormField
              label="Client Secret"
              required
              error={errors.clientSecret?.message}
              helperText="Keep this value secure. It will be encrypted on save."
            >
              <Input
                {...register('clientSecret', { required: 'Client Secret is required' })}
                type="password"
                placeholder="Enter client secret"
                error={errors.clientSecret?.message}
              />
            </FormField>

            {isMicrosoft ? (
              <FormField
                label="Directory (Tenant) ID"
                required
                error={(errors as Record<string, { message?: string }>).entraDirectoryId?.message}
                helperText="Your Azure AD tenant ID"
              >
                <Input
                  {...register('entraDirectoryId' as 'clientId', { required: 'Directory ID is required' })}
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  error={(errors as Record<string, { message?: string }>).entraDirectoryId?.message}
                />
              </FormField>
            ) : (
              <FormField
                label="Hosted Domain"
                error={(errors as Record<string, { message?: string }>).hostedDomain?.message}
                helperText="Restrict to a specific Google Workspace domain (optional)"
              >
                <Input
                  {...register('hostedDomain' as 'clientId')}
                  type="text"
                  placeholder="example.com"
                  error={(errors as Record<string, { message?: string }>).hostedDomain?.message}
                />
              </FormField>
            )}

            <FormField
              label="Allowed Email Domains"
              required
              error={errors.allowedDomains?.message}
              helperText="Comma-separated list of allowed email domains"
            >
              <Input
                {...register('allowedDomains', { required: 'At least one domain is required' })}
                type="text"
                placeholder="example.com, corp.example.com"
                error={errors.allowedDomains?.message}
              />
            </FormField>

            {/* SSO Settings */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">SSO Settings</h4>
              <div className="space-y-3">
                <Checkbox
                  {...register('enforceSSO')}
                  label="Enforce SSO"
                  helperText="When enabled, password login is disabled and users must sign in via this SSO provider."
                />

                <Checkbox
                  {...register('autoProvisionUsers')}
                  label="Auto-provision users"
                  helperText="Automatically create user accounts when new users sign in via SSO for the first time."
                />

                {autoProvisionUsers && roles && roles.length > 0 && (
                  <FormField
                    label="Default roles for auto-provisioned users"
                    helperText="Select roles to assign to users created via SSO auto-provisioning."
                  >
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {roles
                        .filter((r) => !r.isSystemRole)
                        .map((role) => (
                          <Checkbox
                            key={role.roleId}
                            {...register('defaultRoles')}
                            value={role.roleId}
                            label={role.roleName}
                            helperText={role.description}
                          />
                        ))}
                    </div>
                  </FormField>
                )}
              </div>
            </div>
          </form>
        )}

        {/* Step 3: Test Connection */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Test the connection to verify your configuration is correct before activating the provider.
            </p>

            {isTesting && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" label="Testing connection..." />
              </div>
            )}

            {testResult && (
              <div
                className={clsx(
                  'rounded-lg border p-4 flex items-start gap-3',
                  testResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                )}
              >
                {testResult.success ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p
                    className={clsx(
                      'text-sm font-medium',
                      testResult.success ? 'text-green-800' : 'text-red-800'
                    )}
                  >
                    {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </p>
                  <p
                    className={clsx(
                      'text-sm mt-1',
                      testResult.success ? 'text-green-700' : 'text-red-700'
                    )}
                  >
                    {testResult.message}
                  </p>
                  {testResult.details && (
                    <dl className="mt-2 text-xs space-y-1">
                      {testResult.details.issuer && (
                        <div>
                          <dt className="inline font-medium">Issuer: </dt>
                          <dd className="inline">{testResult.details.issuer}</dd>
                        </div>
                      )}
                      {testResult.details.authorizationEndpoint && (
                        <div>
                          <dt className="inline font-medium">Auth Endpoint: </dt>
                          <dd className="inline">{testResult.details.authorizationEndpoint}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              </div>
            )}

            {!isTesting && !testResult && (
              <div className="text-center py-8">
                <Button
                  variant="secondary"
                  onClick={handleTestConnection}
                  disabled={isTesting}
                >
                  Run Connection Test
                </Button>
              </div>
            )}

            {!isTesting && testResult && !testResult.success && (
              <div className="text-center">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTestResult(null);
                    setStep(2);
                  }}
                >
                  Go Back and Fix Configuration
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Activate */}
        {step === 4 && (
          <div className="space-y-4 text-center py-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircleIcon className="h-10 w-10 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Provider Configured Successfully
            </h3>
            <p className="text-sm text-gray-600">
              Your {isMicrosoft ? 'Microsoft Entra ID' : 'Google'} SSO provider has been configured.
              Users in your organization can now sign in using their{' '}
              {isMicrosoft ? 'Microsoft' : 'Google'} accounts.
            </p>
            <Alert variant="info">
              You can manage the provider status, edit settings, or configure enforcement from the SSO settings page.
            </Alert>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step > 1 && step < 4 && (
          <Button variant="secondary" onClick={handleBack} disabled={isSubmitting}>
            Back
          </Button>
        )}

        {step === 1 && (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleNext}>
              I have completed these steps
            </Button>
          </>
        )}

        {step === 2 && (
          <Button
            type="submit"
            form="sso-config-form"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save Configuration
          </Button>
        )}

        {step === 3 && (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={isTesting}
          >
            {testResult?.success ? 'Continue' : 'Skip Test'}
          </Button>
        )}

        {step === 4 && (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SSOProviderSetupWizard;
